import React, { useEffect, useState } from "react";
import { X, Sparkles, Mail, Calendar, MessageSquare, RefreshCw, ExternalLink, ChevronRight, Workflow, Play, Pause, StopCircle, Send, Flame, Newspaper, Phone, FileAudio, Bot } from "lucide-react";
import { Button } from "./ui/button";
import { Sheet, SheetContent } from "./ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Textarea } from "./ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import api from "../lib/api";
import { toast } from "sonner";
import ScoreBadge from "./ScoreBadge";
import AIDraftModal from "./AIDraftModal";
import SequencePreviewModal from "./SequencePreviewModal";

const STAGES = ["new", "enriched", "personalized", "approved", "in_sequence", "engaged", "qualified", "meeting_booked", "meeting_held", "opportunity", "client_won", "nurture"];

function BookingEmailPanel({ aiCallId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const load = async () => {
    if (data || loading) return;
    setLoading(true);
    try {
      const { data: out } = await api.get(`/ai-calls/${aiCallId}/booking-email`);
      setData(out);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load booking email");
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    if (!data?.body_text) return;
    try {
      await navigator.clipboard.writeText(`Subject: ${data.subject}\n\nTo: ${data.to}\n\n${data.body_text}`);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy failed — try selecting + Cmd/Ctrl+C manually");
    }
  };

  return (
    <div className="mt-3 border border-primary/40 bg-primary/5 p-3" style={{ borderRadius: 2 }} data-testid="booking-email-panel">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="overline">Booking confirmation email</div>
        {!open ? (
          <Button size="sm" variant="outline" onClick={() => { setOpen(true); load(); }} data-testid="show-booking-email" style={{ borderRadius: 2 }}>
            <Mail className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} /> Show & send
          </Button>
        ) : (
          <Button size="sm" variant="ghost" onClick={() => setOpen(false)} style={{ borderRadius: 2 }}>Hide</Button>
        )}
      </div>
      {open && loading && <div className="text-xs text-muted-foreground italic">Loading draft…</div>}
      {open && data && (
        <div className="space-y-2">
          <div className="text-xs"><span className="text-muted-foreground">To:</span> <span className="font-mono">{data.to}</span></div>
          <div className="text-xs"><span className="text-muted-foreground">Subject:</span> <span className="font-medium">{data.subject}</span></div>
          <div className="text-sm whitespace-pre-wrap bg-background p-3 border border-border max-h-72 overflow-y-auto" data-testid="booking-email-body" style={{ borderRadius: 2 }}>{data.body_text}</div>
          {data.delivery_error && (
            <div className="text-xs text-destructive">Auto-send failed: {data.delivery_error}. Use one of the buttons below to send manually.</div>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <a href={data.gmail_compose_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 bg-foreground text-background hover:opacity-90" data-testid="send-via-gmail" style={{ borderRadius: 2 }}>
              <Mail className="h-3.5 w-3.5" strokeWidth={1.5} /> Send via Gmail
            </a>
            <a href={data.outlook_compose_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 border border-border hover:bg-accent" data-testid="send-via-outlook" style={{ borderRadius: 2 }}>
              <Mail className="h-3.5 w-3.5" strokeWidth={1.5} /> Send via Outlook
            </a>
            <a href={data.mailto_url} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 border border-border hover:bg-accent" data-testid="send-via-mailto" style={{ borderRadius: 2 }}>
              <Mail className="h-3.5 w-3.5" strokeWidth={1.5} /> Default mail app
            </a>
            <button onClick={copy} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 border border-border hover:bg-accent" data-testid="copy-booking-email" style={{ borderRadius: 2 }}>
              Copy text
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


function PhoneEditor({ lead, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(lead.phone || "");
  const [saving, setSaving] = useState(false);

  React.useEffect(() => { setVal(lead.phone || ""); }, [lead.phone]);

  const save = async () => {
    const v = (val || "").trim();
    if (v && !/^\+?[0-9 ()-]{6,20}$/.test(v)) {
      toast.error("Use international format, e.g. +61412345678");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/leads/${lead.lead_id}`, { phone: v || null });
      toast.success(v ? "Phone saved" : "Phone cleared");
      setEditing(false);
      onUpdated && onUpdated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save phone");
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <button
        onClick={() => setEditing(true)}
        data-testid="edit-phone-trigger"
        className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5"
        title="Click to edit"
      >
        <Phone className="h-3 w-3" strokeWidth={1.5} />
        {lead.phone ? <span className="font-mono">{lead.phone}</span> : <span className="italic">Add phone</span>}
      </button>
    );
  }

  return (
    <div className="inline-flex items-center gap-1.5">
      <Phone className="h-3 w-3 text-muted-foreground" strokeWidth={1.5} />
      <input
        autoFocus
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") { setEditing(false); setVal(lead.phone || ""); } }}
        placeholder="+61412345678"
        data-testid="phone-input"
        className="h-6 text-xs font-mono px-2 border border-border rounded-sm bg-background focus:outline-none focus:ring-1 focus:ring-ring w-44"
        disabled={saving}
      />
      <button
        onClick={save}
        disabled={saving}
        data-testid="phone-save"
        className="text-xs px-2 h-6 border border-border rounded-sm hover:bg-accent disabled:opacity-50"
      >
        {saving ? "…" : "Save"}
      </button>
      <button
        onClick={() => { setEditing(false); setVal(lead.phone || ""); }}
        data-testid="phone-cancel"
        className="text-xs text-muted-foreground hover:text-foreground"
      >
        Cancel
      </button>
    </div>
  );
}

function DealAmountEditor({ lead, onUpdated }) {
  // Only show on opportunity / client_won statuses — the only stages where a $ amount matters.
  const showFor = ["opportunity", "meeting_held", "client_won"];
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(lead.deal_amount != null ? String(lead.deal_amount) : "");
  const [saving, setSaving] = useState(false);

  React.useEffect(() => { setVal(lead.deal_amount != null ? String(lead.deal_amount) : ""); }, [lead.deal_amount]);

  if (!showFor.includes(lead.status)) return null;

  const save = async () => {
    const cleaned = (val || "").replace(/[^0-9.]/g, "");
    const num = cleaned ? Number(cleaned) : null;
    if (cleaned && (isNaN(num) || num < 0)) {
      toast.error("Enter a valid AUD amount, e.g. 80000");
      return;
    }
    setSaving(true);
    try {
      const patch = { deal_amount: num };
      // Stamp deal_won_at when status is client_won and no timestamp yet
      if (lead.status === "client_won" && !lead.deal_won_at) {
        patch.deal_won_at = new Date().toISOString();
      }
      await api.patch(`/leads/${lead.lead_id}`, patch);
      toast.success(num != null ? `Deal value $${num.toLocaleString()} saved` : "Deal value cleared");
      setEditing(false);
      onUpdated && onUpdated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save deal amount");
    } finally {
      setSaving(false);
    }
  };

  const display = lead.deal_amount != null ? `$${Number(lead.deal_amount).toLocaleString()}` : "Set deal $";
  const isWon = lead.status === "client_won";

  if (!editing) {
    return (
      <button
        onClick={() => setEditing(true)}
        data-testid="edit-deal-amount-trigger"
        className={`text-xs inline-flex items-center gap-1.5 px-2 py-1 border rounded-sm hover:bg-accent ${isWon && lead.deal_amount ? "border-[hsl(var(--success))] text-[hsl(var(--success))]" : "border-border text-muted-foreground hover:text-foreground"}`}
        title="Click to edit deal value"
      >
        <span className="font-mono">{display}</span>
      </button>
    );
  }

  return (
    <div className="inline-flex items-center gap-1.5">
      <span className="text-xs text-muted-foreground font-mono">A$</span>
      <input
        autoFocus
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") { setEditing(false); setVal(lead.deal_amount != null ? String(lead.deal_amount) : ""); } }}
        placeholder="80000"
        data-testid="deal-amount-input"
        className="h-6 text-xs font-mono px-2 border border-border rounded-sm bg-background focus:outline-none focus:ring-1 focus:ring-ring w-28"
        disabled={saving}
      />
      <button
        onClick={save}
        disabled={saving}
        data-testid="deal-amount-save"
        className="text-xs px-2 h-6 border border-border rounded-sm hover:bg-accent disabled:opacity-50"
      >
        {saving ? "…" : "Save"}
      </button>
      <button
        onClick={() => { setEditing(false); setVal(lead.deal_amount != null ? String(lead.deal_amount) : ""); }}
        data-testid="deal-amount-cancel"
        className="text-xs text-muted-foreground hover:text-foreground"
      >
        Cancel
      </button>
    </div>
  );
}


function TimelineTab({ leadId }) {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/leads/${leadId}/timeline?limit=200`);
      setItems(data.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load timeline");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => { load(); }, [leadId]);

  if (loading && items === null) return <div className="text-sm text-muted-foreground">Loading timeline…</div>;
  if (!items || items.length === 0) {
    return (
      <div className="text-sm text-muted-foreground">
        No interactions yet. Once you send emails, place calls, run trigger scans, or book meetings, they'll appear here as one chronological stream.
      </div>
    );
  }

  const kindColors = {
    activity: "hsl(var(--muted-foreground))",
    email: "#7c5cff",
    linkedin: "#0a66c2",
    ai_call: "#a04030",
    call: "#a04030",
    meeting: "hsl(var(--success))",
    trigger: "#e0a020",
  };

  const kindLabel = {
    activity: "ACTIVITY", email: "EMAIL", linkedin: "LINKEDIN",
    ai_call: "AI CALL", call: "CALL", meeting: "MEETING", trigger: "TRIGGER",
  };

  return (
    <div className="relative pl-6" data-testid="timeline-stream">
      {/* Vertical rail */}
      <div className="absolute left-2 top-2 bottom-2 w-px bg-border" />
      <div className="text-xs text-muted-foreground mb-3">{items.length} entries · newest first</div>
      <div className="space-y-4">
        {items.map((it, i) => {
          const color = kindColors[it.kind] || "hsl(var(--muted-foreground))";
          const dt = it.at ? new Date(it.at) : null;
          const dateStr = dt ? dt.toLocaleString("en-AU", { timeZone: "Australia/Sydney", dateStyle: "medium", timeStyle: "short" }) : "—";
          return (
            <div key={`${it.kind}-${i}`} className="relative" data-testid={`timeline-${it.kind}-${i}`}>
              <div className="absolute -left-[18px] top-1.5 h-2.5 w-2.5 rounded-full border-2 border-background" style={{ background: color }} />
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-[10px] font-mono tracking-wider uppercase" style={{ color }}>{kindLabel[it.kind] || it.kind}</span>
                <span className="text-[10px] text-muted-foreground/70 font-mono">{it.subkind || ""}</span>
                <span className="text-[10px] text-muted-foreground ml-auto font-mono">{dateStr}</span>
              </div>
              <div className="text-sm mt-1 font-medium leading-snug">{it.title}</div>
              {it.body && <div className="text-xs text-muted-foreground mt-1 leading-relaxed whitespace-pre-wrap line-clamp-4">{it.body}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AccountTab({ lead, onUpdated }) {
  const [contacts, setContacts] = useState(null);
  const [expanding, setExpanding] = useState(false);

  const load = async () => {
    if (!lead.account_id) { setContacts([]); return; }
    try {
      const { data } = await api.get(`/accounts/${lead.account_id}/contacts`);
      setContacts(data.contacts || []);
    } catch {
      setContacts([]);
    }
  };

  React.useEffect(() => { load(); }, [lead.account_id]);

  const expand = async () => {
    setExpanding(true);
    try {
      const { data } = await api.post(`/accounts/expand/${lead.lead_id}`, { max_contacts: 5 });
      if (data.ok) {
        const added = (data.added || []).length;
        if (data.skipped) toast.info("Account already expanded — re-loading contacts");
        else toast.success(`Account expanded · ${added} new contact(s) added`);
        onUpdated && onUpdated();
        await load();
      } else {
        toast.error(`Expansion failed: ${data.error || "unknown"}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Expansion failed");
    } finally {
      setExpanding(false);
    }
  };

  const roleColor = (r) => ({
    cfo: "#a04030", controller: "#7c5cff", ceo: "#0a66c2", coo: "#e0a020",
    ops: "hsl(var(--muted-foreground))", other: "hsl(var(--muted-foreground))",
  }[r] || "hsl(var(--muted-foreground))");

  const roleLabel = { cfo: "CFO", controller: "CONTROLLER", ceo: "CEO/MD", coo: "COO", ops: "OPS", other: "OTHER" };

  return (
    <div className="space-y-4">
      <div className="border border-border p-4" style={{ borderRadius: 2 }}>
        <div className="overline mb-1">Account-based multi-threading</div>
        <div className="font-serif text-base mb-1" style={{ fontWeight: 450 }}>One CFO is good — three decision-makers is better.</div>
        <p className="text-xs text-muted-foreground mb-3">
          Searches Lusha for the <strong className="text-foreground">Financial Controller</strong>, <strong className="text-foreground">CEO/MD</strong> and <strong className="text-foreground">COO</strong> at <span className="font-mono">{lead.company}</span>. New contacts get enrolled with a role-tailored angle, staggered Day +2 / +5 / +7 so the messages don't all land together.
        </p>
        <Button onClick={expand} disabled={expanding || !lead.company} data-testid="expand-account-btn" style={{ borderRadius: 2 }}>
          {expanding ? "Expanding…" : (contacts && contacts.length > 1 ? "Re-run expansion" : "Expand account ↪")}
        </Button>
      </div>

      {contacts && contacts.length > 0 && (
        <div>
          <div className="overline mb-2">Account contacts · {contacts.length}</div>
          <div className="space-y-2">
            {contacts.map(c => (
              <div key={c.lead_id} className="flex items-center justify-between border border-border p-3 text-sm" style={{ borderRadius: 2 }} data-testid={`account-contact-${c.account_role}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-mono tracking-wider uppercase" style={{ color: roleColor(c.account_role) }}>
                      {roleLabel[c.account_role] || c.account_role || "MEMBER"}
                    </span>
                    {c.lead_id === lead.lead_id && <span className="text-[10px] tracking-wider uppercase font-mono text-muted-foreground">· current</span>}
                    {c.lead_id === lead.account_anchor_lead_id && c.lead_id !== lead.lead_id && <span className="text-[10px] tracking-wider uppercase font-mono">· anchor</span>}
                  </div>
                  <div className="font-medium truncate">{c.full_name}</div>
                  <div className="text-xs text-muted-foreground truncate">{c.title} · {c.email || "—"}</div>
                </div>
                <div className="text-xs text-muted-foreground font-mono capitalize ml-3">{(c.status || "").replace(/_/g, " ")}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {contacts !== null && contacts.length === 0 && !lead.account_id && (
        <div className="text-xs text-muted-foreground">No account expansion yet for this company. Click <strong className="text-foreground">Expand account</strong> to bring in the Controller + CEO + COO.</div>
      )}
    </div>
  );
}

function VoiceNoteWidget({ lead, onUpdated }) {
  const [drafting, setDrafting] = React.useState(false);
  const [sending, setSending] = React.useState(false);
  const [draft, setDraft] = React.useState(null);
  const [editedScript, setEditedScript] = React.useState("");
  const eligibleStage = ["connected", "engaged", "dm_sent", "replied"].includes(lead.linkedin_stage);

  const generate = async () => {
    setDrafting(true);
    try {
      const { data } = await api.post(`/linkedin/voice-note/draft/${lead.lead_id}`);
      setDraft(data);
      setEditedScript(data.script || "");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Draft failed");
    } finally {
      setDrafting(false);
    }
  };

  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/linkedin/voice-note/send/${lead.lead_id}`, { script: editedScript });
      if (data.ok) {
        toast.success(`Voice note sent · ~${data.duration_seconds_est}s`);
        setDraft(null);
        setEditedScript("");
        onUpdated && onUpdated();
      } else {
        toast.error(data.error || "Voice send failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="border border-border bg-card p-4 space-y-3" style={{ borderRadius: 2 }} data-testid="voice-note-widget">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <div className="overline">LinkedIn voice note · ElevenLabs</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            25-45 sec audio DM in your cloned voice — typically 3-4× the reply rate of a text DM.
            {!eligibleStage && <span className="text-[hsl(var(--destructive))]"> Connect must be accepted first.</span>}
          </div>
        </div>
        <Button variant="outline" onClick={generate} disabled={drafting || !eligibleStage} data-testid="voice-note-draft-btn" style={{ borderRadius: 2 }} size="sm">
          {drafting ? "Drafting…" : (draft ? "Re-draft" : "Draft script")}
        </Button>
      </div>

      {draft && (
        <div className="space-y-2">
          {draft.rationale && <div className="text-[11px] text-muted-foreground italic">Angle: {draft.rationale}</div>}
          <Textarea value={editedScript} onChange={(e) => setEditedScript(e.target.value)} rows={5} className="input-crisp text-sm" data-testid="voice-note-script-input" placeholder="Voice note script…" />
          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={send} disabled={sending || !editedScript.trim()} data-testid="voice-note-send-btn" style={{ borderRadius: 2 }} size="sm">
              {sending ? "Synthesising + sending…" : "Synthesise & send 🎙️"}
            </Button>
            <span className="text-[11px] text-muted-foreground font-mono">~{Math.round((editedScript || "").split(/\s+/).filter(Boolean).length * 0.4)}s · {(editedScript || "").length} chars</span>
          </div>
        </div>
      )}
    </div>
  );
}

function LiveCoachPanel({ lead }) {
  const [sessionId, setSessionId] = React.useState(null);
  const [turns, setTurns] = React.useState([]);
  const [input, setInput] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [listening, setListening] = React.useState(false);
  const recognitionRef = React.useRef(null);

  const submit = async () => {
    const text = (input || "").trim();
    if (!text) return;
    setLoading(true);
    try {
      const { data } = await api.post("/coach/turn", {
        lead_id: lead.lead_id,
        prospect_said: text,
        session_id: sessionId,
      });
      if (data.ok) {
        if (!sessionId) setSessionId(data.session_id);
        setTurns(prev => [data.turn, ...prev]);
        setInput("");
      } else {
        toast.error(data.error || "Coach failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Coach failed");
    } finally {
      setLoading(false);
    }
  };

  const choose = async (turn, opt) => {
    try {
      await api.post("/coach/choose", {
        session_id: sessionId,
        turn_id: turn.turn_id,
        chosen_response: opt.line,
        chosen_type: opt.type,
      });
      setTurns(prev => prev.map(t => t.turn_id === turn.turn_id ? { ...t, chosen_response: opt.line, chosen_type: opt.type } : t));
      toast.success("Locked in — coach has more context now");
    } catch {
      toast.error("Failed to record choice");
    }
  };

  const end = async () => {
    if (!sessionId) return;
    try {
      await api.post("/coach/end", { session_id: sessionId, outcome: "completed" });
      toast.success("Coach session ended");
      setSessionId(null);
      setTurns([]);
    } catch {
      toast.error("Failed to end session");
    }
  };

  const toggleMic = () => {
    if (listening) {
      recognitionRef.current && recognitionRef.current.stop();
      setListening(false);
      return;
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      toast.error("Live mic not supported — use Chrome/Safari, or just type what they said");
      return;
    }
    const rec = new SR();
    rec.continuous = false;
    rec.interimResults = true;
    rec.lang = "en-AU";
    let finalText = "";
    rec.onresult = (e) => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const tr = e.results[i][0].transcript;
        if (e.results[i].isFinal) finalText += tr + " ";
        else interim += tr;
      }
      setInput((finalText || "") + interim);
    };
    rec.onend = () => { setListening(false); };
    rec.onerror = (ev) => { setListening(false); toast.error(`Mic error: ${ev.error}`); };
    rec.start();
    recognitionRef.current = rec;
    setListening(true);
  };

  return (
    <div className="space-y-4" data-testid="live-coach-panel">
      <div className="border border-border p-4" style={{ borderRadius: 2 }}>
        <div className="overline mb-1">Live AI negotiation coach</div>
        <div className="font-serif text-base mb-2" style={{ fontWeight: 450 }}>What did the prospect just say?</div>
        <p className="text-xs text-muted-foreground mb-3">
          Type or dictate what {lead.full_name?.split(' ')[0] || 'the prospect'} just said. In 1-2 seconds you'll get three response options: <strong className="text-foreground">Empathise & Pivot</strong>, <strong className="text-foreground">Evidence</strong>, and <strong className="text-foreground">Close</strong>. Click whichever you used so the coach gets sharper as the call progresses.
        </p>
        <div className="flex flex-col gap-2">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) submit(); }}
            rows={3}
            placeholder="e.g. 'Honestly we already work with a procurement firm and they handle this' …"
            className="input-crisp text-sm"
            data-testid="coach-input"
          />
          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={submit} disabled={loading || !input.trim()} data-testid="coach-submit" style={{ borderRadius: 2 }} size="sm">
              {loading ? "Coaching…" : "Get 3 options ↵"}
            </Button>
            <Button variant="outline" onClick={toggleMic} data-testid="coach-mic" style={{ borderRadius: 2 }} size="sm">
              {listening ? "● Stop mic" : "🎤 Dictate"}
            </Button>
            <span className="text-[10px] text-muted-foreground font-mono">⌘+Enter to submit</span>
            {sessionId && (
              <Button variant="ghost" onClick={end} data-testid="coach-end" size="sm" className="ml-auto text-xs">End session</Button>
            )}
          </div>
        </div>
      </div>

      {turns.map((t, ti) => (
        <div key={t.turn_id} className="border border-border" style={{ borderRadius: 2 }} data-testid={`coach-turn-${ti}`}>
          <div className="bg-muted/40 px-4 py-2 border-b border-border flex items-baseline justify-between flex-wrap gap-2">
            <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
              {ti === 0 ? "● LIVE" : "earlier"} · intent: <span className="text-foreground">{t.intent}</span>
            </span>
            <span className="text-[10px] text-muted-foreground font-mono">{new Date(t.at).toLocaleTimeString()}</span>
          </div>
          <div className="p-4 space-y-3">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-1">Prospect said</div>
              <div className="text-sm italic">"{t.prospect_said}"</div>
              {t.summary && <div className="text-xs text-muted-foreground mt-1">↳ {t.summary}</div>}
            </div>
            {t.warning && (
              <div className="text-xs bg-[hsl(var(--destructive))]/10 border border-[hsl(var(--destructive))]/30 px-2 py-1.5" style={{ borderRadius: 2 }}>
                ⚠ {t.warning}
              </div>
            )}
            <div className="space-y-2">
              {(t.options || []).map((opt, oi) => {
                const chosen = t.chosen_response === opt.line;
                const colors = ["#7c5cff", "#0a66c2", "#a04030"];
                return (
                  <button
                    key={oi}
                    onClick={() => choose(t, opt)}
                    disabled={!sessionId}
                    data-testid={`coach-option-${ti}-${oi}`}
                    className="w-full text-left border p-3 hover:bg-accent transition-colors"
                    style={{ borderRadius: 2, borderColor: chosen ? "hsl(var(--success))" : "hsl(var(--border))", background: chosen ? "hsl(var(--success) / 0.05)" : undefined }}
                  >
                    <div className="text-[10px] font-mono uppercase tracking-wider mb-1" style={{ color: colors[oi] || "hsl(var(--muted-foreground))" }}>
                      {opt.type} {chosen && <span className="text-[hsl(var(--success))]">· ★ used</span>}
                    </div>
                    <div className="text-sm">"{opt.line}"</div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}



export default function LeadDrawer({ lead, onClose, onUpdated, defaultTab = "research" }) {
  const [activities, setActivities] = useState([]);
  const [emails, setEmails] = useState([]);
  const [reseaching, setResearching] = useState(false);
  const [draftOpen, setDraftOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [classifyText, setClassifyText] = useState("");
  const [classifyResult, setClassifyResult] = useState(null);
  const [classifying, setClassifying] = useState(false);
  const [meetingDate, setMeetingDate] = useState("");
  const [enrollment, setEnrollment] = useState(null);
  const [liTouches, setLiTouches] = useState([]);
  const [liDraft, setLiDraft] = useState(null);
  const [liDraftKind, setLiDraftKind] = useState("connect_note");
  const [liDrafting, setLiDrafting] = useState(false);
  const [triggers, setTriggers] = useState([]);
  const [scanningTriggers, setScanningTriggers] = useState(false);
  const [calls, setCalls] = useState([]);
  const [voicemails, setVoicemails] = useState([]);
  const [aiCalls, setAiCalls] = useState([]);
  const [aiCallStatus, setAiCallStatus] = useState({ ai_call_provider: "vapi", vapi_connected: false, bland_connected: false });
  const [calling, setCalling] = useState(false);
  const [aiCalling, setAiCalling] = useState(false);
  const [dropping, setDropping] = useState(false);

  useEffect(() => {
    if (!lead) return;
    (async () => {
      try {
        const [a, e, en, li, tr, cl, vm, ac, acs] = await Promise.all([
          api.get(`/leads/${lead.lead_id}/activities`),
          api.get("/emails", { params: { lead_id: lead.lead_id } }),
          api.get("/enrollments", { params: { lead_id: lead.lead_id, status: "all" } }),
          api.get("/linkedin/touches", { params: { lead_id: lead.lead_id } }),
          api.get("/triggers", { params: { lead_id: lead.lead_id, limit: 20 } }),
          api.get("/calls", { params: { lead_id: lead.lead_id, limit: 20 } }),
          api.get("/voicemails", { params: { lead_id: lead.lead_id, limit: 20 } }),
          api.get("/ai-calls", { params: { lead_id: lead.lead_id, limit: 20 } }),
          api.get("/ai-calls/status"),
        ]);
        setActivities(a.data);
        setEmails(e.data);
        const active = en.data.find((x) => x.status === "active" || x.status === "paused") || en.data[0] || null;
        setEnrollment(active);
        setLiTouches(li.data);
        setTriggers(tr.data);
        setCalls(cl.data);
        setVoicemails(vm.data);
        setAiCalls(ac.data || []);
        setAiCallStatus(acs.data || { ai_call_provider: "vapi", vapi_connected: false, bland_connected: false });
      } catch {}
    })();
    setClassifyText("");
    setClassifyResult(null);
    setLiDraft(null);
  }, [lead]);

  // Auto-refresh AI calls every 10s when the drawer is open and the lead has
  // any in-flight call (status not yet "completed"). Stops as soon as the
  // call ends so we don't poll forever. Also refreshes once 15s after launching
  // a new AI call so the transcript appears without manual refresh.
  React.useEffect(() => {
    if (!lead) return;
    const inFlight = aiCalls.some((c) => c.status && !["completed", "failed", "ended"].includes(c.status));
    if (!inFlight) return;
    const id = setInterval(async () => {
      try {
        const ac = await api.get("/ai-calls", { params: { lead_id: lead.lead_id, limit: 20 } });
        setAiCalls(ac.data || []);
      } catch {}
    }, 10000);
    return () => clearInterval(id);
  }, [lead, aiCalls]);

  const refreshAiCalls = async () => {
    if (!lead) return;
    try {
      const [ac, acs] = await Promise.all([
        api.get("/ai-calls", { params: { lead_id: lead.lead_id, limit: 20 } }),
        api.get("/ai-calls/status"),
      ]);
      setAiCalls(ac.data || []);
      setAiCallStatus(acs.data || { ai_call_provider: "vapi", vapi_connected: false, bland_connected: false });
      toast.success(`Refreshed — ${ac.data?.length || 0} AI call${ac.data?.length === 1 ? "" : "s"} loaded`);
    } catch (err) {
      toast.error("Refresh failed");
    }
  };

  if (!lead) return null;

  const runResearch = async () => {
    setResearching(true);
    try {
      const { data } = await api.post(`/leads/${lead.lead_id}/research`);
      onUpdated(data);
      toast.success("AI research complete");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Research failed");
    } finally {
      setResearching(false);
    }
  };

  const reEnrich = async () => {
    try {
      const { data } = await api.post(`/leads/${lead.lead_id}/re-enrich`);
      onUpdated(data);
      toast.success("Firmographics refreshed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Re-enrich failed");
    }
  };

  const pushToInstantly = async () => {
    try {
      const { data } = await api.post("/instantly/add-lead", { lead_id: lead.lead_id });
      toast.success("Pushed to Instantly campaign");
      const a = await api.get(`/leads/${lead.lead_id}/activities`);
      setActivities(a.data);
      const { data: ld } = await api.get(`/leads/${lead.lead_id}`);
      onUpdated(ld);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Instantly push failed — check Settings");
    }
  };

  const scanTriggers = async () => {
    setScanningTriggers(true);
    try {
      const { data } = await api.post(`/triggers/scan/${lead.lead_id}`);
      const tr = await api.get("/triggers", { params: { lead_id: lead.lead_id, limit: 20 } });
      setTriggers(tr.data);
      toast.success(`Found ${data.items_found} via ${data.source || "no source"} · ${data.items_saved} new${data.has_hot_trigger ? " · 🔥 HOT" : ""}`);
      const { data: ld } = await api.get(`/leads/${lead.lead_id}`);
      onUpdated(ld);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Scan failed");
    } finally {
      setScanningTriggers(false);
    }
  };

  const dismissTrigger = async (trigger_id) => {
    try {
      await api.patch(`/triggers/${trigger_id}/dismiss`);
      setTriggers((arr) => arr.filter((t) => t.trigger_id !== trigger_id));
    } catch {
      toast.error("Dismiss failed");
    }
  };

  const placeCall = async () => {
    setCalling(true);
    try {
      const { data } = await api.post("/calls/initiate", { lead_id: lead.lead_id });
      toast.success("Twilio dialing — pick up your phone.");
      const cl = await api.get("/calls", { params: { lead_id: lead.lead_id, limit: 20 } });
      setCalls(cl.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Call failed — check Twilio settings & lead phone.");
    } finally {
      setCalling(false);
    }
  };

  const aiProviderConnected = aiCallStatus.ai_call_provider === "bland" ? aiCallStatus.bland_connected : aiCallStatus.vapi_connected;

  const placeAiCall = async () => {
    setAiCalling(true);
    try {
      const { data } = await api.post("/ai-calls/initiate", { lead_id: lead.lead_id });
      toast.success(`AI calling via ${data.provider} — transcript will appear here once the call ends.`);
      const [ac, a] = await Promise.all([
        api.get("/ai-calls", { params: { lead_id: lead.lead_id, limit: 20 } }),
        api.get(`/leads/${lead.lead_id}/activities`),
      ]);
      setAiCalls(ac.data || []);
      setActivities(a.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI call failed — check provider keys & lead phone.");
    } finally {
      setAiCalling(false);
    }
  };

  const dropVoicemail = async () => {
    setDropping(true);
    try {
      await api.post("/voicemails/drop", { lead_id: lead.lead_id });
      toast.success("Voicemail synthesised and dropping…");
      const vm = await api.get("/voicemails", { params: { lead_id: lead.lead_id, limit: 20 } });
      setVoicemails(vm.data);
      const a = await api.get(`/leads/${lead.lead_id}/activities`);
      setActivities(a.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Voicemail drop failed");
    } finally {
      setDropping(false);
    }
  };

  const updateStatus = async (status) => {
    try {
      const { data } = await api.patch(`/leads/${lead.lead_id}`, { status });
      onUpdated(data);
      toast.success(`Status → ${status.replace(/_/g, " ")}`);
    } catch {
      toast.error("Failed to update status");
    }
  };

  const sendEmail = async (email_id) => {
    try {
      const { data } = await api.post(`/emails/${email_id}/send`);
      toast.success(data.simulated ? "Email sent (simulated — add Resend key to send for real)" : "Email sent via Resend");
      const e = await api.get("/emails", { params: { lead_id: lead.lead_id } });
      setEmails(e.data);
      const a = await api.get(`/leads/${lead.lead_id}/activities`);
      setActivities(a.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    }
  };

  const classifyReply = async () => {
    if (!classifyText.trim()) return;
    setClassifying(true);
    try {
      const { data } = await api.post("/ai/classify-reply", { lead_id: lead.lead_id, inbound: classifyText });
      setClassifyResult(data);
      toast.success("Classified");
    } catch (err) {
      toast.error("Classification failed");
    } finally {
      setClassifying(false);
    }
  };

  const bookMeeting = async () => {
    if (!meetingDate) {
      toast.error("Pick a date/time first");
      return;
    }
    try {
      const iso = new Date(meetingDate).toISOString();
      await api.post("/meetings", { lead_id: lead.lead_id, scheduled_at: iso, duration_minutes: 25, location: "Microsoft Teams" });
      toast.success("Meeting booked");
      const { data } = await api.get(`/leads/${lead.lead_id}`);
      onUpdated(data);
    } catch {
      toast.error("Booking failed");
    }
  };

  const refreshEnrollment = async () => {
    try {
      const en = await api.get("/enrollments", { params: { lead_id: lead.lead_id, status: "all" } });
      setEnrollment(en.data.find((x) => x.status === "active" || x.status === "paused") || en.data[0] || null);
    } catch {}
  };

  const enroll = async () => {
    try {
      await api.post("/enrollments", { lead_id: lead.lead_id });
      toast.success("Enrolled — scheduler will advance this sequence automatically");
      refreshEnrollment();
      const { data } = await api.get(`/leads/${lead.lead_id}`);
      onUpdated(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Enrollment failed");
    }
  };

  const setEnrollmentStatus = async (status) => {
    if (!enrollment) return;
    try {
      await api.patch(`/enrollments/${enrollment.enrollment_id}`, { status });
      toast.success(status);
      refreshEnrollment();
    } catch { toast.error("Failed"); }
  };

  const stopEnrollment = async () => {
    if (!enrollment) return;
    try {
      await api.delete(`/enrollments/${enrollment.enrollment_id}`);
      toast.success("Stopped");
      refreshEnrollment();
    } catch { toast.error("Failed"); }
  };

  const refreshLiTouches = async () => {
    try {
      const { data } = await api.get("/linkedin/touches", { params: { lead_id: lead.lead_id } });
      setLiTouches(data);
    } catch {}
  };

  const logTouch = async (type, body) => {
    try {
      await api.post("/linkedin/touches", { lead_id: lead.lead_id, type, body });
      toast.success(`Logged ${type.replace(/_/g, " ")}`);
      refreshLiTouches();
      const { data } = await api.get(`/leads/${lead.lead_id}`);
      onUpdated(data);
    } catch { toast.error("Failed"); }
  };

  const draftLinkedIn = async () => {
    setLiDrafting(true);
    try {
      const { data } = await api.post("/linkedin/draft", { lead_id: lead.lead_id, kind: liDraftKind });
      setLiDraft(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Draft failed");
    } finally {
      setLiDrafting(false);
    }
  };

  const copyLiDraft = async () => {
    if (!liDraft?.body) return;
    try {
      await navigator.clipboard.writeText(liDraft.body);
      toast.success("Copied to clipboard — paste into LinkedIn");
    } catch {
      toast.error("Copy failed");
    }
  };

  const research = lead.ai_research || {};
  const hasResearch = research.outreach_angle || research.opening_line;

  return (
    <Sheet open={!!lead} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-[640px] overflow-y-auto p-0" data-testid="lead-drawer">
        <div className="px-6 py-5 border-b border-border bg-background sticky top-0 z-10">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="overline mb-1">{lead.industry}</div>
              <div className="font-serif text-2xl tracking-tight" style={{ fontWeight: 400 }}>{lead.full_name}</div>
              <div className="text-sm text-muted-foreground truncate">{lead.title} · {lead.company}</div>
            </div>
            <button onClick={onClose} data-testid="drawer-close" className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" strokeWidth={1.5} /></button>
          </div>

          <div className="mt-4 flex items-center gap-3 flex-wrap">
            <ScoreBadge score={lead.score} band={lead.score_band} />
            <Select value={lead.status} onValueChange={updateStatus}>
              <SelectTrigger className="w-[180px] h-8 input-crisp text-xs" data-testid="drawer-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STAGES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}
              </SelectContent>
            </Select>
            {lead.linkedin_url && (
              <a href={lead.linkedin_url} target="_blank" rel="noreferrer" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
                LinkedIn <ExternalLink className="h-3 w-3" strokeWidth={1.5} />
              </a>
            )}
            <PhoneEditor lead={lead} onUpdated={onUpdated} />
            <DealAmountEditor lead={lead} onUpdated={onUpdated} />
          </div>

          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <Button size="sm" onClick={runResearch} disabled={reseaching} data-testid="run-research" style={{ borderRadius: 2 }}>
              <Sparkles className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} />
              {reseaching ? "Researching…" : hasResearch ? "Re-run research" : "Run AI research"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setDraftOpen(true)} data-testid="open-draft" style={{ borderRadius: 2 }}>
              <Mail className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} /> AI Draft email
            </Button>
            <Button size="sm" variant="outline" onClick={() => setPreviewOpen(true)} data-testid="open-sequence-preview" style={{ borderRadius: 2 }}>
              <Sparkles className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} /> Preview full sequence
            </Button>
            <Button size="sm" variant="outline" onClick={reEnrich} data-testid="re-enrich" style={{ borderRadius: 2 }}>
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} /> Re-enrich
            </Button>
            <Button size="sm" variant="outline" onClick={pushToInstantly} data-testid="push-instantly" style={{ borderRadius: 2 }}>
              <Send className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} /> Push to Instantly
            </Button>
            <Button size="sm" variant="outline" onClick={placeCall} disabled={calling || !lead.phone} data-testid="place-call" style={{ borderRadius: 2 }}>
              <Phone className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} /> {calling ? "Dialing…" : lead.phone ? "Call" : "Call (no phone)"}
            </Button>
            <Button size="sm" variant="outline" onClick={dropVoicemail} disabled={dropping || !lead.phone} data-testid="drop-voicemail" style={{ borderRadius: 2 }}>
              <Phone className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} /> {dropping ? "Synthesising…" : "Drop voicemail"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={placeAiCall}
              disabled={aiCalling || !lead.phone || !aiProviderConnected}
              data-testid="place-ai-call"
              style={{ borderRadius: 2 }}
              title={!aiProviderConnected ? `Connect ${aiCallStatus.ai_call_provider || "vapi"} in Settings to enable AI calling` : ""}
            >
              <Bot className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} />
              {aiCalling
                ? "Dialing AI…"
                : !lead.phone
                  ? "AI Call (no phone)"
                  : !aiProviderConnected
                    ? `AI Call (${aiCallStatus.ai_call_provider || "vapi"} not connected)`
                    : `AI Call · ${aiCallStatus.ai_call_provider || "vapi"}`}
            </Button>
          </div>
        </div>

        <Tabs defaultValue={defaultTab} className="px-6 py-4">
          <TabsList className="bg-transparent p-0 border-b border-border w-full justify-start gap-6 h-auto rounded-none flex-wrap">
            <TabsTrigger value="research" className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-foreground rounded-none px-0 pb-2" data-testid="tab-research">AI Research</TabsTrigger>
            <TabsTrigger value="triggers" className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-foreground rounded-none px-0 pb-2" data-testid="tab-triggers">
              Triggers
              {lead.has_hot_trigger && <Flame className="h-3 w-3 ml-1 inline text-primary" strokeWidth={1.5} />}
            </TabsTrigger>
            <TabsTrigger value="conversations" className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-foreground rounded-none px-0 pb-2" data-testid="tab-conversations">Conversations</TabsTrigger>
            <TabsTrigger value="calls" className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-foreground rounded-none px-0 pb-2" data-testid="tab-calls">
              Calls{calls.length > 0 && <span className="ml-1 text-xs font-mono text-muted-foreground">{calls.length}</span>}
            </TabsTrigger>
            <TabsTrigger value="linkedin" className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-foreground rounded-none px-0 pb-2" data-testid="tab-linkedin">LinkedIn</TabsTrigger>
            <TabsTrigger value="activity" className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-foreground rounded-none px-0 pb-2" data-testid="tab-activity">Activity</TabsTrigger>
            <TabsTrigger value="timeline" className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-foreground rounded-none px-0 pb-2" data-testid="tab-timeline">Timeline</TabsTrigger>
            <TabsTrigger value="account" className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-foreground rounded-none px-0 pb-2" data-testid="tab-account">Account</TabsTrigger>
            <TabsTrigger value="coach" className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-foreground rounded-none px-0 pb-2" data-testid="tab-coach">Live coach</TabsTrigger>
            <TabsTrigger value="qualify" className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-foreground rounded-none px-0 pb-2" data-testid="tab-qualify">Reply Triage</TabsTrigger>
            <TabsTrigger value="sequence" className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-foreground rounded-none px-0 pb-2" data-testid="tab-sequence">Sequence</TabsTrigger>
            <TabsTrigger value="meeting" className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-foreground rounded-none px-0 pb-2" data-testid="tab-meeting">Book Meeting</TabsTrigger>
          </TabsList>

          <TabsContent value="research" className="mt-6 space-y-5" data-testid="research-content">
            {!hasResearch && (
              <div className="text-sm text-muted-foreground">No research yet. Click <strong className="text-foreground">Run AI research</strong> above.</div>
            )}
            {hasResearch && (
              <>
                <Section label="Outreach angle" body={research.outreach_angle} accent />
                <Section label="Opening line" mono body={research.opening_line} />
                <Section label="Company summary" body={research.company_summary} />
                <Section label="Industry analysis" body={research.industry_analysis} />                <Section label="CFO profile" body={research.cfo_profile} />
                <Section label="Trigger event" body={research.trigger_event} />
                <ListSection label="Pain points" items={research.pain_points} />
                <ListSection label="Likely cost categories" items={research.cost_categories} />
                <Section label="Suggested next action" body={research.suggested_next_action} />
                <Section label="Score rationale" body={research.rationale} />
              </>
            )}
          </TabsContent>

          <TabsContent value="triggers" className="mt-6 space-y-4" data-testid="triggers-content">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="text-sm text-muted-foreground">Live news / SEC / RSS scan for {lead.company}. AI-ranked as CFO outreach triggers.</div>
                {lead.triggers_last_scan_at && (
                  <div className="text-xs text-muted-foreground mt-1 font-mono">
                    Last scan: {new Date(lead.triggers_last_scan_at).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })}
                  </div>
                )}
              </div>
              <Button size="sm" onClick={scanTriggers} disabled={scanningTriggers} data-testid="scan-triggers" style={{ borderRadius: 2 }}>
                <Newspaper className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} /> {scanningTriggers ? "Scanning…" : "Scan now"}
              </Button>
            </div>
            {triggers.length === 0 && !scanningTriggers && (
              <div className="text-sm text-muted-foreground">No triggers yet. Click <strong className="text-foreground">Scan now</strong> — uses your Perplexity key first, then Tavily, then free Google News.</div>
            )}
            <div className="space-y-3">
              {triggers.map((t) => (
                <div key={t.trigger_id} className={`border bg-card p-4 ${t.score === "high" ? "border-primary" : "border-border"}`} style={{ borderRadius: 2 }} data-testid={`trigger-${t.trigger_id}`}>
                  <div className="flex items-start justify-between gap-3 mb-1.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] uppercase tracking-widest font-mono px-1.5 py-0.5 ${t.score === "high" ? "bg-primary text-primary-foreground" : t.score === "med" ? "bg-secondary text-foreground" : "bg-secondary/40 text-muted-foreground"}`} style={{ borderRadius: 2 }}>
                        {t.score === "high" && <Flame className="h-2.5 w-2.5 inline mr-0.5" strokeWidth={2} />}
                        {t.score}
                      </span>
                      <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">{t.category?.replace(/_/g, " ")}</span>
                      {t.source_host && <span className="text-[10px] text-muted-foreground font-mono">{t.source_host}</span>}
                      {t.source && <span className="text-[10px] text-muted-foreground font-mono opacity-60">via {t.source}</span>}
                    </div>
                    <button onClick={() => dismissTrigger(t.trigger_id)} className="text-muted-foreground hover:text-foreground" data-testid={`dismiss-${t.trigger_id}`}>
                      <X className="h-3 w-3" strokeWidth={1.5} />
                    </button>
                  </div>
                  {t.title && <div className="text-sm font-medium mb-1">{t.title}</div>}
                  {t.angle && <div className="text-sm border-l-2 border-primary pl-3 my-2 italic">{t.angle}</div>}
                  {t.snippet && <div className="text-xs text-muted-foreground mb-2 line-clamp-2">{t.snippet}</div>}
                  {t.url && (
                    <a href={t.url} target="_blank" rel="noreferrer" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
                      Read source <ExternalLink className="h-3 w-3" strokeWidth={1.5} />
                    </a>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>


          <TabsContent value="conversations" className="mt-6 space-y-4" data-testid="conversations-content">
            {emails.length === 0 && <div className="text-sm text-muted-foreground">No emails yet. Use <strong className="text-foreground">AI Draft email</strong>.</div>}
            {emails.map((em) => (
              <div key={em.email_id} className="border border-border bg-card p-4" style={{ borderRadius: 2 }} data-testid={`email-${em.email_id}`}>
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="overline">{em.kind} · {em.status}</div>
                  {em.status === "draft" && (
                    <Button size="sm" onClick={() => sendEmail(em.email_id)} data-testid={`send-${em.email_id}`} style={{ borderRadius: 2 }}>Send</Button>
                  )}
                </div>
                <div className="font-medium mb-2">{em.subject}</div>
                <div className="text-sm whitespace-pre-wrap text-foreground/80">{em.body_text}</div>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="calls" className="mt-6 space-y-4" data-testid="calls-content">
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm text-muted-foreground">
                {lead.phone ? <>Phone: <span className="font-mono text-foreground">{lead.phone}</span></> : "No phone number on this lead — add one in the contact section to enable calling."}
              </div>
              <Button size="sm" variant="outline" onClick={refreshAiCalls} data-testid="refresh-ai-calls" style={{ borderRadius: 2 }}>
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} /> Refresh
              </Button>
            </div>

            {voicemails.length > 0 && (
              <div className="space-y-3" data-testid="voicemails-section">
                <div className="overline">AI voicemail drops · {voicemails.length}</div>
                {voicemails.map((v) => (
                  <div key={v.voicemail_id} className="border border-border bg-card p-4" style={{ borderRadius: 2 }} data-testid={`voicemail-${v.voicemail_id}`}>
                    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <Phone className="h-3.5 w-3.5 text-primary" strokeWidth={1.8} />
                        <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">{v.status}</span>
                        {v.duration_seconds != null && <span className="text-xs font-mono">{v.duration_seconds}s</span>}
                        {v.answered_by && <span className="text-[10px] uppercase tracking-widest font-mono px-1.5 py-0.5 bg-secondary text-foreground" style={{ borderRadius: 2 }}>{v.answered_by.replace("_", " ")}</span>}
                      </div>
                      <div className="text-xs text-muted-foreground font-mono">{new Date(v.created_at).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })}</div>
                    </div>
                    {v.script && <div className="text-sm border-l-2 border-primary pl-3 italic mb-2 text-foreground/85">"{v.script}"</div>}
                    {v.audio_url && <audio controls src={v.audio_url} className="w-full max-w-md" />}
                    {v.error && <div className="text-xs text-destructive mt-1">{v.error}</div>}
                  </div>
                ))}
              </div>
            )}

            {calls.length === 0 && voicemails.length === 0 && aiCalls.length === 0 && (
              <div className="text-sm text-muted-foreground">No calls or voicemails yet. Click <strong className="text-foreground">Call</strong> to dial live, <strong className="text-foreground">Drop voicemail</strong> for an AI message, or <strong className="text-foreground">AI Call</strong> for an autonomous 2-way AI conversation.</div>
            )}

            {aiCalls.length > 0 && (
              <div className="space-y-3" data-testid="ai-calls-section">
                <div className="overline pt-2">Live AI calls · {aiCalls.length}</div>
                {aiCalls.map((c) => (
                  <div key={c.ai_call_id} className="border border-border bg-card p-4" style={{ borderRadius: 2 }} data-testid={`ai-call-${c.ai_call_id}`}>
                    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <Bot className="h-3.5 w-3.5 text-primary" strokeWidth={1.8} />
                        <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">{c.provider}</span>
                        <span className="text-[10px] uppercase tracking-widest font-mono px-1.5 py-0.5 bg-secondary text-foreground" style={{ borderRadius: 2 }}>{c.status}</span>
                        {c.duration_seconds != null && c.duration_seconds > 0 && (
                          <span className="text-xs font-mono">{Math.floor(c.duration_seconds / 60)}m {c.duration_seconds % 60}s</span>
                        )}
                        {c.detected_intent && (
                          <span className="text-[10px] uppercase tracking-widest font-mono px-1.5 py-0.5 bg-primary text-primary-foreground" style={{ borderRadius: 2 }}>{c.detected_intent.replace(/_/g, " ")}</span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground font-mono">{new Date(c.created_at).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })}</div>
                    </div>
                    <div className="text-xs text-muted-foreground mb-1">→ {c.to_phone}</div>
                    {c.first_message && (
                      <div className="text-xs italic text-muted-foreground mb-2">Opening: "{c.first_message}"</div>
                    )}
                    {c.summary && (
                      <div className="text-sm border-l-2 border-primary pl-3 italic text-foreground/85 mb-2">{c.summary}</div>
                    )}
                    {c.recording_url && (
                      <audio controls src={c.recording_url} className="w-full mb-2 max-w-md" />
                    )}
                    {c.transcript && (
                      <details className="mt-2" open>
                        <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
                          <FileAudio className="h-3 w-3" strokeWidth={1.5} /> Full transcript ({c.transcript.length} chars)
                        </summary>
                        <div className="mt-2 text-xs whitespace-pre-wrap text-foreground bg-secondary/30 p-3 max-h-96 overflow-y-auto border-l-2 border-primary" data-testid="ai-call-transcript">{c.transcript}</div>
                      </details>
                    )}
                    {c.detected_intent === "meeting_booked" && (
                      <BookingEmailPanel aiCallId={c.ai_call_id} />
                    )}
                  </div>
                ))}
              </div>
            )}

            {calls.length > 0 && <div className="overline pt-2">Live calls · {calls.length}</div>}
            {calls.map((c) => (
              <div key={c.call_log_id} className="border border-border bg-card p-4" style={{ borderRadius: 2 }} data-testid={`call-${c.call_log_id}`}>
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <Phone className="h-3.5 w-3.5 text-primary" strokeWidth={1.8} />
                    <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">{c.status}</span>
                    {c.duration_seconds != null && <span className="text-xs font-mono">{Math.floor(c.duration_seconds / 60)}m {c.duration_seconds % 60}s</span>}
                    {c.ai_summary?.detected_intent && (
                      <span className="text-[10px] uppercase tracking-widest font-mono px-1.5 py-0.5 bg-primary text-primary-foreground" style={{ borderRadius: 2 }}>{c.ai_summary.detected_intent}</span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground font-mono">{new Date(c.created_at).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })}</div>
                </div>
                {c.transcribe_status && c.transcribe_status !== "done" && (
                  <div className="text-xs text-muted-foreground italic mb-2">Status: {c.transcribe_status}{c.transcribe_error ? ` · ${c.transcribe_error}` : ""}</div>
                )}
                {c.recording_url && (
                  <audio controls src={c.recording_url + ".mp3"} className="w-full mb-2 max-w-md" />
                )}
                {c.ai_summary?.summary && (
                  <div className="space-y-2">
                    <div className="text-sm border-l-2 border-primary pl-3 italic text-foreground/85">{c.ai_summary.summary}</div>
                    {c.ai_summary.key_points?.length > 0 && (
                      <div>
                        <div className="overline mb-1">Key points</div>
                        <ul className="text-sm list-disc pl-5 space-y-0.5">
                          {c.ai_summary.key_points.map((p, i) => <li key={i}>{p}</li>)}
                        </ul>
                      </div>
                    )}
                    {c.ai_summary.next_steps?.length > 0 && (
                      <div>
                        <div className="overline mb-1">Next steps</div>
                        <ul className="text-sm list-disc pl-5 space-y-0.5">
                          {c.ai_summary.next_steps.map((p, i) => <li key={i}>{p}</li>)}
                        </ul>
                      </div>
                    )}
                    {c.ai_summary.follow_up_email_subject && (
                      <details className="mt-2">
                        <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">Suggested follow-up email</summary>
                        <div className="mt-2 bg-secondary/40 p-3 font-mono text-xs whitespace-pre-wrap" style={{ borderRadius: 2 }}>
                          <div className="font-medium mb-1">{c.ai_summary.follow_up_email_subject}</div>
                          {c.ai_summary.follow_up_email_body}
                        </div>
                      </details>
                    )}
                  </div>
                )}
                {c.transcript && (
                  <details className="mt-3">
                    <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
                      <FileAudio className="h-3 w-3" strokeWidth={1.5} /> Full transcript
                    </summary>
                    <div className="mt-2 text-xs whitespace-pre-wrap text-muted-foreground max-h-60 overflow-y-auto">{c.transcript}</div>
                  </details>
                )}
              </div>
            ))}
          </TabsContent>


          <TabsContent value="linkedin" className="mt-6 space-y-4" data-testid="linkedin-content">
            {!lead.linkedin_url && (
              <div className="text-sm text-muted-foreground">No LinkedIn URL on this lead. Add one in the lead profile.</div>
            )}
            {lead.linkedin_url && (
              <a href={lead.linkedin_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-foreground hover:text-primary border border-border bg-card px-3 py-2 transition-colors" style={{ borderRadius: 2 }} data-testid="open-linkedin">
                <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.5} /> Open LinkedIn profile
              </a>
            )}

            <VoiceNoteWidget lead={lead} onUpdated={onUpdated} />

            <div className="border border-border bg-card p-4 space-y-3" style={{ borderRadius: 2 }}>
              <div className="overline">Log a touch</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {[
                  { t: "profile_view", l: "Viewed profile" },
                  { t: "connect_sent", l: "Sent connect" },
                  { t: "connect_note_sent", l: "Connect + note" },
                  { t: "connect_accepted", l: "Connect accepted" },
                  { t: "post_like", l: "Liked their post" },
                  { t: "post_comment", l: "Commented" },
                  { t: "dm_sent", l: "DM sent" },
                  { t: "voice_note_sent", l: "Voice note sent" },
                  { t: "inmail_sent", l: "InMail sent" },
                  { t: "dm_received", l: "DM received" },
                ].map((b) => (
                  <Button key={b.t} size="sm" variant="outline" onClick={() => logTouch(b.t)} data-testid={`li-log-${b.t}`} style={{ borderRadius: 2 }}>
                    {b.l}
                  </Button>
                ))}
              </div>
            </div>

            <div className="border border-border bg-card p-4 space-y-3" style={{ borderRadius: 2 }}>
              <div className="flex items-center justify-between">
                <div className="overline">AI message draft</div>
                <select
                  value={liDraftKind}
                  onChange={(e) => setLiDraftKind(e.target.value)}
                  className="bg-background border border-border px-2 py-1 text-xs input-crisp"
                  data-testid="li-kind"
                >
                  <option value="connect_note">Connect note (≤280 chars)</option>
                  <option value="dm_first">First DM</option>
                  <option value="dm_followup">Follow-up DM</option>
                  <option value="dm_meeting_ask">DM meeting ask</option>
                  <option value="comment">Post comment</option>
                  <option value="voice_note_script">Voice note script</option>
                </select>
              </div>
              <Button size="sm" onClick={draftLinkedIn} disabled={liDrafting} data-testid="li-draft" style={{ borderRadius: 2 }}>
                <Sparkles className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} /> {liDrafting ? "Drafting…" : "Generate"}
              </Button>
              {liDraft && (
                <div className="space-y-2 fade-up">
                  <Textarea
                    value={liDraft.body}
                    onChange={(e) => setLiDraft({ ...liDraft, body: e.target.value, char_count: e.target.value.length })}
                    rows={6}
                    className="font-mono text-sm input-crisp"
                    data-testid="li-draft-body"
                  />
                  <div className="flex items-center justify-between">
                    <div className={`text-xs font-mono ${liDraft.kind === "connect_note" && liDraft.char_count > 280 ? "text-destructive" : "text-muted-foreground"}`}>
                      {liDraft.char_count} chars{liDraft.kind === "connect_note" ? " / 280" : ""}
                    </div>
                    <Button size="sm" onClick={copyLiDraft} data-testid="li-copy" style={{ borderRadius: 2 }}>Copy &amp; paste in LinkedIn</Button>
                  </div>
                </div>
              )}
            </div>

            <div>
              <div className="overline mb-2">Touches ({liTouches.length})</div>
              {liTouches.length === 0 && <div className="text-sm text-muted-foreground">No touches logged yet.</div>}
              <div className="space-y-2">
                {liTouches.map((t) => (
                  <div key={t.touch_id} className="flex items-start gap-3 py-2 border-b border-border last:border-0" data-testid={`li-touch-${t.touch_id}`}>
                    <div className="h-2 w-2 mt-1.5 bg-primary shrink-0" style={{ borderRadius: 999 }} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm capitalize">{t.type.replace(/_/g, " ")}</div>
                      {t.body && <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{t.body}</div>}
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground/70 mt-1 font-mono">
                        {new Date(t.created_at).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>


          <TabsContent value="activity" className="mt-6 space-y-3" data-testid="activity-content">
            {activities.length === 0 && <div className="text-sm text-muted-foreground">No activity yet.</div>}
            {activities.map((a) => (
              <div key={a.activity_id} className="flex gap-3 py-2 border-b border-border last:border-0">
                <div className="h-2 w-2 mt-2 bg-primary shrink-0" style={{ borderRadius: 999 }} />
                <div className="flex-1">
                  <div className="text-sm">{a.title}</div>
                  {a.body && <div className="text-xs text-muted-foreground mt-0.5">{a.body}</div>}
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground/70 mt-1">{a.type}</div>
                </div>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="timeline" className="mt-6" data-testid="timeline-content">
            <TimelineTab leadId={lead.lead_id} />
          </TabsContent>

          <TabsContent value="account" className="mt-6" data-testid="account-content">
            <AccountTab lead={lead} onUpdated={onUpdated} />
          </TabsContent>

          <TabsContent value="coach" className="mt-6" data-testid="coach-content">
            <LiveCoachPanel lead={lead} />
          </TabsContent>

          <TabsContent value="qualify" className="mt-6 space-y-3" data-testid="qualify-content">
            <div className="text-sm text-muted-foreground">Paste an inbound reply from this lead. AI will classify intent and draft a suggested response.</div>
            <Textarea value={classifyText} onChange={(e) => setClassifyText(e.target.value)} rows={5} className="input-crisp" placeholder="Hi, thanks for reaching out…" data-testid="classify-input" />
            <Button onClick={classifyReply} disabled={classifying} data-testid="classify-btn" style={{ borderRadius: 2 }}>
              <MessageSquare className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} /> {classifying ? "Classifying…" : "Classify reply"}
            </Button>
            {classifyResult && (
              <div className="border border-border bg-card p-4 space-y-3" style={{ borderRadius: 2 }} data-testid="classify-result">
                <div className="flex items-center justify-between">
                  <div className="overline">Intent</div>
                  <div className="font-mono text-xs uppercase tracking-widest">{classifyResult.intent} · {Math.round(classifyResult.confidence * 100)}%</div>
                </div>
                <div className="text-sm">{classifyResult.reasoning}</div>
                <div className="overline">Suggested action</div>
                <div className="text-sm">{classifyResult.suggested_action}</div>
                {classifyResult.suggested_reply && <>
                  <div className="overline">Suggested reply</div>
                  <div className="text-sm whitespace-pre-wrap font-mono">{classifyResult.suggested_reply}</div>
                </>}
              </div>
            )}
          </TabsContent>

          <TabsContent value="meeting" className="mt-6 space-y-3" data-testid="meeting-content">
            <div className="text-sm text-muted-foreground">Manually book a discovery meeting (or share your Outlook/Calendly link in Settings to send a self-serve booking link in your emails).</div>
            <input
              type="datetime-local"
              value={meetingDate}
              onChange={(e) => setMeetingDate(e.target.value)}
              className="bg-background border border-border px-3 py-2 text-sm input-crisp w-full"
              data-testid="meeting-datetime"
            />
            <Button onClick={bookMeeting} data-testid="book-meeting" style={{ borderRadius: 2 }}>
              <Calendar className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} /> Book 25-min discovery
            </Button>
          </TabsContent>

          <TabsContent value="sequence" className="mt-6 space-y-4" data-testid="sequence-content">
            {!enrollment && (
              <div className="space-y-3">
                <div className="text-sm text-muted-foreground">No active sequence. Enroll this lead in the default 7-step CFO cadence — the scheduler will draft and (if auto-send is on) send each step automatically.</div>
                <Button onClick={enroll} data-testid="enroll-btn" style={{ borderRadius: 2 }}>
                  <Workflow className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} /> Enroll in CFO sequence
                </Button>
              </div>
            )}
            {enrollment && (
              <div className="border border-border bg-card p-4 space-y-3" style={{ borderRadius: 2 }}>
                <div className="flex items-center justify-between">
                  <div className="overline">Status</div>
                  <div className="text-xs font-mono uppercase tracking-widest">{enrollment.status}</div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="overline">Step</div>
                  <div className="text-sm">{enrollment.current_step + 1} / 7</div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="overline">Next send</div>
                  <div className="text-sm font-mono">
                    {enrollment.next_send_at ? new Date(enrollment.next_send_at).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" }) : "—"}
                  </div>
                </div>
                <div className="flex gap-2 pt-2 border-t border-border">
                  {enrollment.status === "active" && (
                    <Button size="sm" variant="outline" onClick={() => setEnrollmentStatus("paused")} data-testid="seq-pause" style={{ borderRadius: 2 }}>
                      <Pause className="h-3 w-3 mr-1" strokeWidth={1.5} /> Pause
                    </Button>
                  )}
                  {enrollment.status === "paused" && (
                    <Button size="sm" variant="outline" onClick={() => setEnrollmentStatus("active")} data-testid="seq-resume" style={{ borderRadius: 2 }}>
                      <Play className="h-3 w-3 mr-1" strokeWidth={1.5} /> Resume
                    </Button>
                  )}
                  {(enrollment.status === "active" || enrollment.status === "paused") && (
                    <Button size="sm" variant="outline" onClick={stopEnrollment} data-testid="seq-stop" style={{ borderRadius: 2 }}>
                      <StopCircle className="h-3 w-3 mr-1" strokeWidth={1.5} /> Stop
                    </Button>
                  )}
                  {(enrollment.status === "stopped" || enrollment.status === "completed") && (
                    <Button size="sm" onClick={enroll} data-testid="seq-reenroll" style={{ borderRadius: 2 }}>
                      <Workflow className="h-3 w-3 mr-1" strokeWidth={1.5} /> Re-enroll
                    </Button>
                  )}
                </div>
              </div>
            )}
            <div className="text-xs text-muted-foreground">
              The scheduler ticks every 60 seconds. The sequence auto-pauses when the lead replies, books a meeting, or moves to nurture.
            </div>
          </TabsContent>
        </Tabs>

        <AIDraftModal
          open={draftOpen}
          onClose={() => setDraftOpen(false)}
          lead={lead}
          onSaved={async () => {
            const e = await api.get("/emails", { params: { lead_id: lead.lead_id } });
            setEmails(e.data);
            const { data } = await api.get(`/leads/${lead.lead_id}`);
            onUpdated(data);
          }}
        />

        <SequencePreviewModal
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          lead={lead}
          autoGenerate={true}
        />
      </SheetContent>
    </Sheet>
  );
}

function Section({ label, body, mono, accent }) {
  if (!body) return null;
  return (
    <div className={accent ? "border-l-2 border-primary pl-4" : ""}>
      <div className="overline mb-1.5">{label}</div>
      <div className={`text-sm ${mono ? "font-mono" : ""} text-foreground/90`}>{body}</div>
    </div>
  );
}

function ListSection({ label, items }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className="overline mb-2">{label}</div>
      <ul className="space-y-1.5">
        {items.map((it, i) => (
          <li key={i} className="text-sm flex gap-2">
            <ChevronRight className="h-3.5 w-3.5 text-primary shrink-0 mt-1" strokeWidth={1.5} />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
